import { styled } from '@mui/material/styles';
import { ImgixImage } from '@Components/ui/ImgixImage';
import { Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from '@Src/providers/TranslationProvider';
import MobileAppBadges from '@Components/Widgets/Apps/MobileAppBadges';
import { StyledSection } from '../../WebsiteV2/Common/StyledSection';
import { useDesignSchema } from '@Src/providers/DesignSchemaProvider';

const StyledPaper = styled(Paper)`
  border-radius: 0;
  box-shadow: none;
  background-color: ${({ theme }) => theme.palette.tertiary?.main};
  color: ${({ theme }) => theme.palette.tertiary?.contrastText};
  padding: ${({ theme }) => theme.spacing(3)};
  display: 'flex';
  align-items: 'center';
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: ${({ theme }) => theme.spacing(6)};
    margin-right: ${({ theme }) => theme.spacing(6)};
  }
`;

const StyledImgixImage = styled(ImgixImage)`
  position: relative;
  z-index: 1;
  height: auto;
  ${({ theme }) => theme.breakpoints.up('md')} {
    left: ${({ theme }) => theme.spacing(6)} !important;
    top: ${({ theme }) => theme.spacing(4)} !important;
    width: 90%;
  }
  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-top: 0;
    width: 100%;
  }
`;

const StyledCarouselGrid = styled(Grid)`
  text-align: end;
  padding: 0px !important;
  margin: 0px !important;
  height: 100%;
`;

const StyledTitleWrapper = styled(Grid)`
  padding-top: 0;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding-top: ${({ theme }) => theme.spacing(2)};
  }
  padding-bottom: ${({ theme }) => theme.spacing(2)};
`;

const StyledTextGrid = styled(Grid)`
  position: relative;
  width: 90%;
  ${({ theme }) => theme.breakpoints.up('md')} {
    left: ${({ theme }) => theme.spacing(6)} !important;
  }
  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-top: ${({ theme }) => theme.spacing(4)};
    padding-right: ${({ theme }) => theme.spacing(4)};
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding-top: ${({ theme }) => theme.spacing(2)};
    padding-right: ${({ theme }) => theme.spacing(2)};
  }
`;

const StyledTextSection = styled(Grid)``;

const StyledAppBadgeContainer = styled(Grid)`
  gap: ${({ theme }) => theme.spacing(2)} !important;
  margin-top: ${({ theme }) => theme.spacing(4)} !important;
  margin-bottom: ${({ theme }) => theme.spacing(4)} !important;
`;

type AppsProps = {
  className?: string;
};

export const Apps = ({ className }: AppsProps): JSX.Element => {
  const { mobileApps, imageAttributes } = useDesignSchema('v2');

  const { translate } = useTranslation();
  const hasImages = (mobileApps?.images?.length || 0) > 0;
  const image = mobileApps?.images?.[0] || '';
  const src = image ? `${image}?w=600` : '';
  return (
    <StyledSection className={className}>
      <StyledPaper elevation={3}>
        <Grid container>
          <StyledTextSection
            item
            xs={12}
            md={hasImages ? 6 : 12}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <StyledTextGrid container>
              {mobileApps?.title && (
                <StyledTitleWrapper item xs={12}>
                  <Typography variant="h2Display">
                    {translate(mobileApps?.title)}
                  </Typography>
                </StyledTitleWrapper>
              )}

              {mobileApps?.description && (
                <StyledTitleWrapper item xs={12}>
                  <Typography>{translate(mobileApps?.description)}</Typography>
                </StyledTitleWrapper>
              )}

              <StyledAppBadgeContainer
                item
                display={'flex'}
                justifyContent={'start'}
                alignItems={'center'}
                xs={12}
                gap={2}
                marginTop={4}
                marginBottom={4}
              >
                <MobileAppBadges />
              </StyledAppBadgeContainer>
            </StyledTextGrid>
          </StyledTextSection>
          {hasImages && (
            <StyledCarouselGrid item xs={12} md={6}>
              <StyledImgixImage
                key={`image-0`}
                data-fd={`app-image-0`}
                src={src}
                alt={imageAttributes?.[image]?.alt || `app image 0`}
              />
            </StyledCarouselGrid>
          )}
        </Grid>
      </StyledPaper>
    </StyledSection>
  );
};

export default Apps;
